// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import type { Middleware } from 'openapi-fetch';
import { useAuthStore } from '@/app/stores/auth-store';
import { refreshAccessToken } from '@/app/features/auth/hooks/useAuth';

/** Paths that should never have an Authorization header attached. */
const PUBLIC_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/login/verify-code',
  '/api/v1/auth/login/resend-code',
  '/api/v1/auth/register',
  '/api/v1/auth/refresh',
  '/healthz',
  '/readyz',
];

/** Flag to prevent concurrent refresh attempts. */
let isRefreshing = false;
let refreshPromise: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

/**
 * Stores a clone of each request before it is consumed by fetch.
 * This allows us to retry with an unconsumed body on 401 refresh.
 */
const requestClones = new WeakMap<Request, Request>();

/**
 * openapi-fetch middleware that:
 * 1. Attaches the Authorization header to authenticated requests.
 * 2. Intercepts 401 responses, attempts token refresh, and retries once.
 */
export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const url = new URL(request.url);
    const isPublic = PUBLIC_PATHS.some((p) => url.pathname === p);
    if (isPublic) return request;

    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      // Save one clone for potential 401 retry (unconsumed body), then
      // mutate the original request to attach the auth header.
      // Important: clone ONCE only — double-clone breaks FormData/stream
      // bodies because the first clone locks the underlying ReadableStream.
      requestClones.set(request, request.clone());
      request.headers.set('Authorization', `Bearer ${accessToken}`);
      return request;
    }

    return request;
  },

  async onResponse({ request, response }) {
    // Only intercept 401s on non-auth endpoints.
    if (response.status !== 401) return response;

    const url = new URL(request.url);
    const isAuthEndpoint = PUBLIC_PATHS.some((p) => url.pathname === p);
    if (isAuthEndpoint) return response;

    // Attempt token refresh (deduplicated across concurrent 401s).
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    const newTokens = await refreshPromise;

    if (!newTokens) {
      // Refresh failed — redirect to login.
      useAuthStore.getState().logout();
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login';
      }
      return response;
    }

    // Retry with the saved clone (whose body stream is still unconsumed).
    const savedClone = requestClones.get(request);
    requestClones.delete(request);
    if (savedClone) {
      savedClone.headers.set('Authorization', `Bearer ${newTokens.accessToken}`);
      try {
        return await fetch(savedClone);
      } catch {
        // Retry can fail for file uploads (proxy connection reset after 401).
        // Token is already refreshed, so the next attempt will succeed.
        // Return a synthetic error response so the caller knows to retry.
        return new Response(
          JSON.stringify({ error: { message: 'Session refreshed. Please try again.' } }),
          { status: 409, headers: { 'Content-Type': 'application/json' } },
        );
      }
    }

    // Fallback: if no clone was saved (shouldn't happen), try without body.
    const headers = new Headers(request.headers);
    headers.set('Authorization', `Bearer ${newTokens.accessToken}`);
    try {
      return await fetch(request.url, {
        method: request.method,
        headers,
        credentials: request.credentials,
        signal: request.signal,
      });
    } catch {
      return new Response(
        JSON.stringify({ error: { message: 'Session refreshed. Please try again.' } }),
        { status: 409, headers: { 'Content-Type': 'application/json' } },
      );
    }
  },
};
